import { buildWilderUrl, handleWilderError, WILDER_CONFIG } from './config.js';

/**
 * Busca todos os assets de carbono da API Wilder
 * @returns {Promise<Array>} Array de assets de carbono
 */
export const fetchCarbonAssets = async () => {
  try {
    const response = await fetch(
      buildWilderUrl(WILDER_CONFIG.ENDPOINTS.ASSETS),
      WILDER_CONFIG.REQUEST_CONFIG
    );
    
    if (!response.ok) {
      throw new Error(`Falha ao carregar os ativos: ${response.status} ${response.statusText}`);
    }
    
    const data = await response.json();
    return data.response.results || [];
  } catch (error) {
    throw new Error(handleWilderError(error, 'fetchCarbonAssets'));
  }
};

/**
 * Filtra assets por emissor específico
 * @param {Array} assets - Array de assets para filtrar
 * @param {string} issuer - Nome do emissor para filtrar
 * @returns {Array} Array filtrado de assets
 */
export const filterAssetsByIssuer = (assets, issuer) => {
  if (!assets || !Array.isArray(assets)) return [];
  if (!issuer) return assets;
  
  return assets.filter(asset => 
    asset.Issuer === issuer
  );
};

/**
 * Busca e filtra assets de carbono por emissor
 * @param {string} issuer - Nome do emissor (ex: 'VERT ECOTECH SA')
 * @returns {Promise<Array>} Array filtrado de assets
 */
export const fetchFilteredCarbonAssets = async (issuer = 'VERT ECOTECH SA') => {
  try {
    const allAssets = await fetchCarbonAssets();
    return filterAssetsByIssuer(allAssets, issuer);
  } catch (error) {
    throw new Error(handleWilderError(error, 'fetchFilteredCarbonAssets'));
  }
};

/**
 * Busca um asset pelo nome (mesmo filtro usado no modal de detalhes).
 * @param {string} assetName - Valor do campo "Asset Name"
 * @returns {Promise<object|null>} Primeiro resultado ou null
 */
export const fetchAssetByName = async (assetName) => {
  if (!assetName) return null;
  try {
    const constraints = JSON.stringify([
      {
        key: 'Asset Name',
        constraint_type: 'text contains',
        value: assetName,
      },
    ]);
    const url = `${buildWilderUrl(WILDER_CONFIG.ENDPOINTS.ASSETS)}?constraints=${encodeURIComponent(
      constraints
    )}`;
    const response = await fetch(url, WILDER_CONFIG.REQUEST_CONFIG);
    if (!response.ok) {
      throw new Error(`Falha ao carregar o ativo: ${response.status} ${response.statusText}`);
    }
    const data = await response.json();
    const results = data.response?.results || [];
    return results[0] ?? null;
  } catch (error) {
    throw new Error(handleWilderError(error, 'fetchAssetByName'));
  }
};

/**
 * Formata número para exibição brasileira
 * @param {number|string} number - Número para formatar
 * @returns {string} Número formatado
 */
export const formatNumber = (number) => {
  if (!number && number !== 0) return '0';
  return new Intl.NumberFormat('pt-BR').format(number);
};

/**
 * Formata data para exibição brasileira
 * @param {string} dateString - String de data para formatar
 * @returns {string} Data formatada ou 'N/A' se inválida
 */
export const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  } catch {
    return 'N/A';
  }
};

/**
 * Trunca texto para um comprimento máximo
 * @param {string} text - Texto para truncar
 * @param {number} maxLength - Comprimento máximo (padrão: 60)
 * @returns {string} Texto truncado
 */
export const truncateText = (text, maxLength = 60) => {
  if (!text || text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
};
